import { supabase } from '@/utils/supabase';

// ─── Payment Gateway Types ─────────────────────────────────────────────────

export type PaymentGateway = 'bkash' | 'nagad' | 'cod';

// Full payment config with credentials — returned ONLY by edge functions
// (service role key). Frontend admin never reads these back.
export interface PaymentConfig {
  id: string;
  gateway: PaymentGateway;
  is_active: boolean;
  app_key: string | null;
  app_secret: string | null;
  merchant_id: string | null;
  merchant_username: string | null;
  merchant_password: string | null;
  api_base_url: string;
  api_callback_url: string | null;
  has_credentials: boolean;
  created_at: string;
  updated_at: string;
}

// Safe config without credentials — for admin UI and non-admin consumers
// Credentials are NEVER returned. Use updateCredentials() to write them.
export interface SafePaymentConfig {
  id: string;
  gateway: PaymentGateway;
  is_active: boolean;
  api_base_url: string;
  api_callback_url: string | null;
  has_credentials: boolean;
  created_at: string;
  updated_at: string;
}

// Credentials payload for write-only update
export interface PaymentCredentials {
  app_key: string;
  app_secret: string;
  merchant_id?: string;
  merchant_username?: string;
  merchant_password?: string;
}

// ─── Payment Service Types ─────────────────────────────────────────────────

export interface InitiatePaymentResult {
  success: boolean;
  paymentId?: string;
  gatewayUrl?: string;
  redirectUrl?: string;
  error?: string;
  rawResponse?: Record<string, unknown> | unknown;
}

export interface VerifyPaymentResult {
  success: boolean;
  transactionId?: string;
  amount?: string;
  customerPhone?: string;
  status?: string;
  error?: string;
  rawResponse?: Record<string, unknown> | unknown;
}

export interface RefundPaymentResult {
  success: boolean;
  refundTrxId?: string;
  error?: string;
  rawResponse?: Record<string, unknown> | unknown;
}

// ─── Payment Service Factory ───────────────────────────────────────────────
// SECURITY FIX: All payment API calls now go through the payment-proxy edge
// function. Credentials are stored server-side and never exposed to the browser.
//
// WRITE-ONLY CREDENTIALS: Admin UI can SET credentials via updateCredentials()
// but never READ them back. has_credentials flag shows if they are configured.

class PaymentService {
  // ─── Generic Payment Flow (via edge function) ─────────────────────────

  async initiatePayment(
    gateway: PaymentGateway,
    amount: number,
    orderId: string,
    callbackUrl: string
  ): Promise<InitiatePaymentResult> {
    try {
      const action = gateway === 'bkash' ? 'bkash_create_payment' : 'nagad_create_payment';
      const payload = gateway === 'bkash'
        ? { amount: amount.toFixed(2), invoice_number: orderId, callback_url: callbackUrl }
        : { amount: amount.toFixed(2), order_id: orderId, callback_url: callbackUrl };

      const { data, error } = await supabase.functions.invoke('payment-proxy', {
        body: { action, gateway, payload },
      });

      if (error || !data) {
        return {
          success: false,
          error: error?.message || 'Payment initiation failed',
          rawResponse: data,
        };
      }

      if (data.error) {
        return {
          success: false,
          error: data.error,
          rawResponse: data,
        };
      }

      return {
        success: data.statusCode === '0000' || data.statusCode === '000' || data.status === 'Success',
        paymentId: data.paymentID || data.paymentReferenceId,
        gatewayUrl: data.bkashURL || data.callBackUrl,
        redirectUrl: data.bkashURL || data.callBackUrl,
        error: data.statusMessage || data.message,
        rawResponse: data,
      };
    } catch (err: unknown) {
      return {
        success: false,
        error: err instanceof Error ? err.message : 'Payment initiation error',
        rawResponse: err,
      };
    }
  }

  /**
   * Execute a payment after the user has approved it on the gateway page.
   * This is REQUIRED for bKash tokenized checkout — it captures the money.
   * Must be called BEFORE querying/recording the payment.
   */
  async executePayment(
    gateway: PaymentGateway,
    paymentId: string
  ): Promise<VerifyPaymentResult> {
    try {
      const action = gateway === 'bkash' ? 'bkash_execute_payment' : 'nagad_verify_payment';
      const payload = gateway === 'bkash'
        ? { payment_id: paymentId }
        : { payment_ref_id: paymentId };

      const { data, error } = await supabase.functions.invoke('payment-proxy', {
        body: { action, gateway, payload },
      });

      if (error || !data) {
        return {
          success: false,
          error: error?.message || 'Payment execution failed',
          rawResponse: data,
        };
      }

      if (data.error) {
        return {
          success: false,
          error: data.error,
          rawResponse: data,
        };
      }

      return {
        success: data.statusCode === '0000' || data.statusCode === '000' || data.status === 'Success',
        transactionId: data.trxID || data.paymentReferenceId,
        amount: data.amount,
        customerPhone: data.customerMsisdn || data.client_mobile_number,
        status: data.transactionStatus || data.status,
        error: data.statusMessage || data.message,
        rawResponse: data,
      };
    } catch (err: unknown) {
      return {
        success: false,
        error: err instanceof Error ? err.message : 'Payment execution error',
        rawResponse: err,
      };
    }
  }

  async verifyPayment(
    gateway: PaymentGateway,
    paymentId: string
  ): Promise<VerifyPaymentResult> {
    try {
      const action = gateway === 'bkash' ? 'bkash_query_payment' : 'nagad_verify_payment';
      const payload = gateway === 'bkash'
        ? { payment_id: paymentId }
        : { payment_ref_id: paymentId };

      const { data, error } = await supabase.functions.invoke('payment-proxy', {
        body: { action, gateway, payload },
      });

      if (error || !data) {
        return {
          success: false,
          error: error?.message || 'Payment verification failed',
          rawResponse: data,
        };
      }

      if (data.error) {
        return {
          success: false,
          error: data.error,
          rawResponse: data,
        };
      }

      return {
        success: data.statusCode === '0000' || data.statusCode === '000' || data.status === 'Success',
        transactionId: data.trxID || data.paymentReferenceId,
        amount: data.amount,
        customerPhone: data.customerMsisdn || data.client_mobile_number,
        status: data.transactionStatus || data.status,
        error: data.statusMessage || data.message,
        rawResponse: data,
      };
    } catch (err: unknown) {
      return {
        success: false,
        error: err instanceof Error ? err.message : 'Payment verification error',
        rawResponse: err,
      };
    }
  }

  /**
   * Atomic server-side payment completion.
   * Calls the edge function's `complete_payment` action which does
   * execute → verify → record in one shot. No client manipulation possible.
   */
  async completePayment(
    gateway: PaymentGateway,
    orderId: string,
    paymentId: string
  ): Promise<{ success: boolean; error?: string; transactionId?: string; trxId?: string; alreadyPaid?: boolean }> {
    try {
      const { data, error } = await supabase.functions.invoke('payment-proxy', {
        body: {
          action: 'complete_payment',
          payload: { order_id: orderId, gateway, payment_id: paymentId },
        },
      });

      if (error) {
        return { success: false, error: error.message || 'Payment completion failed' };
      }

      return {
        success: data?.success === true,
        error: data?.error,
        transactionId: data?.transaction_id,
        trxId: data?.trx_id,
        alreadyPaid: data?.already_paid,
      };
    } catch (err: unknown) {
      return {
        success: false,
        error: err instanceof Error ? err.message : 'Payment completion error',
      };
    }
  }

  async recordPaymentToDB(
    orderId: string,
    gateway: PaymentGateway,
    amount: number,
    paymentId: string | undefined,
    trxId: string | undefined,
    status: string,
    gatewayResponse: Record<string, unknown> | unknown
  ): Promise<string | null> {
    try {
      const { data, error } = await supabase.functions.invoke('payment-proxy', {
        body: {
          action: 'record_payment',
          payload: {
            order_id: orderId,
            gateway,
            amount,
            payment_id: paymentId || null,
            trx_id: trxId || null,
            status,
            gateway_response: gatewayResponse || {},
          },
        },
      });

      if (error || !data?.transaction_id) {
        console.error('[Payment] Failed to record payment:', error);
        return null;
      }
      return data.transaction_id;
    } catch (err: unknown) {
      console.error('[Payment] Failed to record payment:', err);
      return null;
    }
  }

  // ─── Config Management (Safe — no credentials) ───────────────────────

  // Returns FULL config — only use when absolutely necessary (e.g. edge function
  // or admin export). Prefer getAllPaymentConfigs() for UI.
  async getPaymentConfig(gateway: PaymentGateway): Promise<PaymentConfig | null> {
    const { data, error } = await supabase
      .from('payment_configs')
      .select('*')
      .eq('gateway', gateway)
      .single();

    if (error) {
      console.error(`[Payment] Failed to load ${gateway} config:`, error);
      return null;
    }
    return data as PaymentConfig;
  }

  async getSafePaymentConfig(gateway: PaymentGateway): Promise<SafePaymentConfig | null> {
    const { data, error } = await supabase
      .from('payment_configs')
      .select('id, gateway, is_active, api_base_url, api_callback_url, has_credentials, created_at, updated_at')
      .eq('gateway', gateway)
      .single();

    if (error) {
      console.error(`[Payment] Failed to load ${gateway} safe config:`, error);
      return null;
    }
    return data as SafePaymentConfig;
  }

  // Returns SAFE configs only. Credentials are never exposed to the browser.
  async getAllPaymentConfigs(): Promise<SafePaymentConfig[]> {
    const { data, error } = await supabase
      .from('payment_configs')
      .select('id, gateway, is_active, api_base_url, api_callback_url, has_credentials, created_at, updated_at')
      .order('gateway');

    if (error) {
      console.error('[Payment] Failed to load configs:', error);
      return [];
    }
    return (data || []) as SafePaymentConfig[];
  }

  // Update non-credential fields (api_base_url, api_callback_url, is_active, etc.)
  async updatePaymentConfig(gateway: PaymentGateway, updates: Partial<SafePaymentConfig>): Promise<boolean> {
    const { error } = await supabase
      .from('payment_configs')
      .update(updates)
      .eq('gateway', gateway);

    return !error;
  }

  // WRITE-ONLY: Update credentials. These are NOT returned in getAllPaymentConfigs().
  // has_credentials is set to true so the UI knows they are configured.
  async updateCredentials(gateway: PaymentGateway, credentials: PaymentCredentials): Promise<boolean> {
    const { error } = await supabase
      .from('payment_configs')
      .update({
        ...credentials,
        has_credentials: true,
        updated_at: new Date().toISOString(),
      })
      .eq('gateway', gateway);

    if (error) {
      console.error('[Payment] Failed to update credentials:', error);
      return false;
    }
    return true;
  }

  // Optional: Clear credentials (e.g. when switching accounts or deactivating)
  async clearCredentials(gateway: PaymentGateway): Promise<boolean> {
    const { error } = await supabase
      .from('payment_configs')
      .update({
        app_key: null,
        app_secret: null,
        merchant_id: null,
        merchant_username: null,
        merchant_password: null,
        has_credentials: false,
        updated_at: new Date().toISOString(),
      })
      .eq('gateway', gateway);

    return !error;
  }

  async setActivePaymentGateway(gateway: PaymentGateway, active: boolean): Promise<boolean> {
    const { error } = await supabase
      .from('payment_configs')
      .update({ is_active: active, updated_at: new Date().toISOString() })
      .eq('gateway', gateway);

    return !error;
  }
}

// ─── Singleton Export ───────────────────────────────────────────────────

export const paymentService = new PaymentService();

// ─── Payment Configs Table (SQL to create) ───────────────────────────────
// This should be added to the database schema:
/*
create table if not exists public.payment_configs (
  id uuid default gen_random_uuid() primary key,
  gateway text not null check (gateway in ('bkash', 'nagad', 'cod')),
  is_active boolean default false,
  app_key text,
  app_secret text,
  merchant_id text,
  merchant_username text,
  merchant_password text,
  api_base_url text not null,
  api_callback_url text,
  has_credentials boolean default false,
  created_at timestamp with time zone default now(),
  updated_at timestamp with time zone default now(),
  constraint unique_payment_gateway unique (gateway)
);

alter table public.payment_configs enable row level security;

create policy "Payment configs are viewable by admins." on payment_configs
  for select using ( exists (select 1 from profiles where id = auth.uid() and role in ('admin', 'manager')) );

create policy "Payment configs are manageable by admins." on payment_configs
  for all using ( exists (select 1 from profiles where id = auth.uid() and role in ('admin', 'manager')) );

insert into public.payment_configs (gateway, api_base_url, is_active) values
  ('bkash', 'https://tokenized.sandbox.bka.sh', false),
  ('nagad', 'https://sandbox-api.mynagad.com', false)
  on conflict (gateway) do nothing;
*/